import React, { ReactNode, useCallback, useState } from 'react';
import cn from 'classnames';
import ConditionalWrapper from '@components/ConditionalWrapper';
import { Collapse, Tooltip } from '@mui/material';
import { ChevronDoubleDownIcon } from '@heroicons/react/outline';

type ShadowCardProps = {
    children: ReactNode;
    className?: string;
    Icon?: JSX.Element;
    maxHeight?: number;
    minHeight?: number;
    showExpand?: boolean;
    title?: React.ReactNode | string;
    tooltipText?: string;
    hideTooltip?: boolean;
    noFullHeight?: boolean;
    noOverflow?: boolean;
};
const ShadowCard = ({
    children,
    className,
    Icon,
    maxHeight,
    minHeight,
    showExpand = false,
    title,
    tooltipText,
    hideTooltip,
    noFullHeight,
    noOverflow,
}: ShadowCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => setExpanded(!expanded);
    const renderTooltipWrapper = useCallback(
        (children: React.ReactNode) => (
            <Tooltip title={!hideTooltip ? (tooltipText ?? '') : ''} placement="top" arrow enterDelay={400}>
                <div className="absolute right-3 top-3">{children}</div>
            </Tooltip>
        ),
        [hideTooltip],
    );
    const renderCollapseWrapper = useCallback(
        (children: React.ReactNode) => (
            <Collapse in={expanded} collapsedSize={minHeight ?? 270} timeout={700}>
                {children}
            </Collapse>
        ),
        [expanded, minHeight],
    );
    return (
        <div
            className={cn(
                'rounded-2xl border-b border-black border-opacity-5 bg-white p-6 pt-4 shadow-2xl',
                'relative flex min-h-[100px] flex-col',
                {
                    'h-full flex-1': !noFullHeight,
                },
                maxHeight ? `max-h-[${maxHeight}px]` : null,
                className,
            )}
        >
            {title && (
                <div className="flex items-center justify-center pb-3 pt-1">
                    <h2 className="text-xl font-semibold tracking-tight text-black">{title}</h2>
                </div>
            )}
            {Icon && (
                <ConditionalWrapper wrapper={renderTooltipWrapper} condition={!!tooltipText}>
                    {Icon}
                </ConditionalWrapper>
            )}
            <ConditionalWrapper condition={showExpand} wrapper={renderCollapseWrapper}>
                <div className={cn({ 'h-full overflow-auto': !noOverflow })}>{children}</div>
            </ConditionalWrapper>
            {showExpand && (
                <div
                    className="absolute -bottom-4 cursor-pointer self-center rounded-full border border-gray-200 bg-white p-1"
                    onClick={toggleExpanded}
                >
                    <ChevronDoubleDownIcon
                        height={20}
                        width={20}
                        className={cn('transition duration-700', {
                            'rotate-180': expanded,
                        })}
                    />
                </div>
            )}
        </div>
    );
};

export default ShadowCard;
